/**
 * Glend GA4 Enhanced Ecommerce Data Layer
 * Pushes ecommerce events to window.dataLayer for GTM/GA4
 */
(function () {
  'use strict';

  window.dataLayer = window.dataLayer || [];

  // --- Consent gating ---
  function readConsent() {
    var m = document.cookie.match(/(^| )glend_cookie_consent=([^;]+)/);
    return m ? m[2] : null;
  }

  var consentGranted = readConsent() === 'accepted';
  // Single ordered queue preserves user-journey order across ecommerce and raw events.
  // Each entry: { kind: 'ec', event, ecommerce } or { kind: 'raw', payload }.
  var pendingQueue = [];
  var PENDING_CAP = 50;

  function flushPending() {
    if (!consentGranted) return;
    pendingQueue.forEach(function (entry) {
      if (entry.kind === 'ec') {
        var ec = typeof entry.ecommerce === 'function' ? entry.ecommerce() : entry.ecommerce;
        if (!ec) return; // resolver returned null -> skip (e.g., zero impressions)
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({ event: entry.event, ecommerce: ec });
      } else {
        window.dataLayer.push(entry.payload);
      }
    });
    pendingQueue.length = 0;
  }

  // Enqueue an ecommerce event whose payload is resolved at flush time.
  // Used by trackViewItemList to hold a chronological slot for the first
  // pre-consent impression, so the consolidated view_item_list event fires
  // in journey order (before later view_item / add_to_cart events).
  function enqueueDeferred(event, resolver) {
    if (consentGranted) return null;
    if (pendingQueue.length >= PENDING_CAP) return null;
    var slot = { kind: 'ec', event: event, ecommerce: resolver };
    pendingQueue.push(slot);
    return slot;
  }

  function push(event, ecommerce) {
    if (!consentGranted) {
      if (pendingQueue.length < PENDING_CAP) {
        pendingQueue.push({ kind: 'ec', event: event, ecommerce: ecommerce });
      }
      return;
    }
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({ event: event, ecommerce: ecommerce });
  }

  function pushRaw(payload) {
    if (!consentGranted) {
      if (pendingQueue.length < PENDING_CAP) {
        pendingQueue.push({ kind: 'raw', payload: payload });
      }
      return;
    }
    window.dataLayer.push(payload);
  }

  window.addEventListener('glend:consent-granted', function () {
    consentGranted = true;
    flushPending();
  });
  window.addEventListener('glend:consent-denied', function () {
    consentGranted = false;
    pendingQueue.length = 0;
  });

  function centsToEur(cents) {
    return Number((cents / 100).toFixed(2));
  }

  function getListContext() {
    var ctx = window.glendListContext || {};
    return {
      item_list_id: ctx.item_list_id || '',
      item_list_name: ctx.item_list_name || ''
    };
  }

  function getSelectedVariantId() {
    var url = new URL(window.location.href);
    var v = url.searchParams.get('variant');
    if (v) return v;
    var meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
    if (meta && meta.product && meta.product.variants && meta.product.variants[0]) {
      return meta.product.variants[0].id;
    }
    return '';
  }

  // --- view_item: fires on product pages ---
  function trackViewItem() {
    var meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
    if (!meta || meta.page.pageType !== 'product') return;

    var el = document.querySelector('product-info');
    if (!el) return;

    var variantId = getSelectedVariantId();
    var productId = el.dataset.productId || (meta.product && meta.product.id);
    var title = document.querySelector('.product__title h1');
    var priceEl = document.querySelector('.product__info-wrapper .price__regular .price-item--regular, .product__info-wrapper .price-item--sale');
    var vendor = meta.product ? meta.product.vendor : '';
    var variantPrice = meta.product && meta.product.variants && meta.product.variants[0]
      ? meta.product.variants[0].price
      : 0;

    var price = 0;
    if (priceEl) {
      var priceText = priceEl.textContent.replace(/[^\d.,]/g, '').replace(',', '.');
      price = parseFloat(priceText) || 0;
    }
    if (!price && variantPrice) {
      price = centsToEur(variantPrice);
    }

    push('view_item', {
      currency: 'EUR',
      value: price,
      items: [{
        item_id: String(variantId || productId),
        item_name: title ? title.textContent.trim() : '',
        item_brand: vendor,
        item_variant: meta.product && meta.product.variants && meta.product.variants[0]
          ? (meta.product.variants[0].public_title || meta.product.variants[0].title || '')
          : '',
        price: price
      }]
    });
  }

  // --- add_to_cart / remove_from_cart: subscribe to cart-update pub/sub ---
  // Snapshot used to diff cart state for remove detection
  var lastCartSnapshot = {};

  function snapshotCart(items) {
    var snap = {};
    (items || []).forEach(function (it) {
      snap[it.variant_id] = {
        quantity: it.quantity,
        product_id: it.product_id,
        product_title: it.product_title,
        variant_title: it.variant_title || '',
        price: it.price
      };
    });
    return snap;
  }

  function primeCartSnapshot() {
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) { lastCartSnapshot = snapshotCart(cart.items); })
      .catch(function () {});
  }

  function trackCartMutations() {
    if (typeof subscribe !== 'function') return;

    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (!event || !event.cartData) return;
      var source = event.source || '';

      var addSources = ['product-form', 'sticky-add-to-cart', 'quick-add', 'cart-upsell'];
      var isAdd = addSources.some(function (s) { return source.indexOf(s) !== -1; });

      if (isAdd) {
        var item = event.cartData;
        if (item.product_title) {
          push('add_to_cart', {
            currency: 'EUR',
            value: centsToEur(item.price * item.quantity),
            items: [{
              item_id: String(item.variant_id),
              item_name: item.product_title,
              item_variant: item.variant_title || '',
              price: centsToEur(item.price),
              quantity: item.quantity
            }]
          });
        }
      }

      // After any cart mutation, diff against last snapshot to find removed/reduced items
      if (source === 'cart-items') {
        fetch('/cart.js')
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            var newSnap = snapshotCart(cart.items);
            Object.keys(lastCartSnapshot).forEach(function (vid) {
              var prev = lastCartSnapshot[vid];
              var curr = newSnap[vid];
              var prevQty = prev.quantity || 0;
              var currQty = curr ? curr.quantity : 0;
              if (currQty < prevQty) {
                var removedQty = prevQty - currQty;
                push('remove_from_cart', {
                  currency: 'EUR',
                  value: centsToEur(prev.price * removedQty),
                  items: [{
                    item_id: String(vid),
                    item_name: prev.product_title,
                    item_variant: prev.variant_title,
                    price: centsToEur(prev.price),
                    quantity: removedQty
                  }]
                });
              }
            });
            lastCartSnapshot = newSnap;
          })
          .catch(function () {});
      } else {
        // For add-type sources, refresh snapshot so future diffs are accurate
        fetch('/cart.js')
          .then(function (r) { return r.json(); })
          .then(function (cart) { lastCartSnapshot = snapshotCart(cart.items); })
          .catch(function () {});
      }
    });
  }

  // --- begin_checkout: click on checkout button ---
  function trackBeginCheckout() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[name="checkout"], #CartDrawer-Checkout, #checkout');
      if (!btn) return;

      fetch('/cart.js')
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var items = cart.items.map(function (item) {
            return {
              item_id: String(item.variant_id),
              item_name: item.product_title,
              item_variant: item.variant_title || '',
              price: centsToEur(item.price),
              quantity: item.quantity
            };
          });
          push('begin_checkout', {
            currency: 'EUR',
            value: centsToEur(cart.total_price),
            items: items
          });
        })
        .catch(function () {});
    });
  }

  // --- view_cart: /cart page load + cart drawer open ---
  function pushViewCart() {
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart.items || !cart.items.length) return;
        var items = cart.items.map(function (item) {
          return {
            item_id: String(item.variant_id),
            item_name: item.product_title,
            item_variant: item.variant_title || '',
            price: centsToEur(item.price),
            quantity: item.quantity
          };
        });
        push('view_cart', {
          currency: 'EUR',
          value: centsToEur(cart.total_price),
          items: items
        });
      })
      .catch(function () {});
  }

  function trackViewCart() {
    if (window.location.pathname === '/cart') {
      pushViewCart();
    }
  }

  // --- view_item_list / select_item: impressions on collection/search ---
  function trackViewItemList() {
    var meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
    if (!meta) return;
    var pageType = meta.page && meta.page.pageType;
    if (pageType !== 'collection' && pageType !== 'search') return;

    var list = getListContext();
    if (!list.item_list_name) {
      if (pageType === 'search') {
        var q = new URL(window.location.href).searchParams.get('q') || '';
        list = { item_list_id: 'search', item_list_name: 'Search: ' + q };
        if (q) {
          pushRaw({ event: 'search', search_term: q });
        }
      } else if (meta.collection) {
        list = {
          item_list_id: meta.collection.handle || String(meta.collection.id || ''),
          item_list_name: meta.collection.title || 'Collection'
        };
      }
    }

    var seen = new Set();
    var pending = [];
    var preConsentImpressions = [];
    var preConsentSlotReserved = false;
    var flushTimer = null;

    function cardPayload(card) {
      var vid = card.dataset.variantId || card.dataset.productId || card.getAttribute('data-depict-product-id') || '';
      return {
        item_id: String(vid),
        item_name: card.dataset.productTitle || '',
        item_brand: card.dataset.productVendor || '',
        price: parseFloat(card.dataset.productPrice || '0') || undefined,
        item_list_id: list.item_list_id,
        item_list_name: list.item_list_name
      };
    }

    function flush() {
      flushTimer = null;
      if (!pending.length) return;
      push('view_item_list', {
        item_list_id: list.item_list_id,
        item_list_name: list.item_list_name,
        items: pending.splice(0)
      });
    }

    window.addEventListener('glend:consent-denied', function () {
      preConsentImpressions.length = 0;
      pending.length = 0;
      if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
    });

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var card = entry.target;
        var vid = card.dataset.variantId || card.dataset.productId;
        if (!vid || seen.has(vid)) {
          io.unobserve(card);
          return;
        }
        seen.add(vid);
        io.unobserve(card);
        if (consentGranted) {
          pending.push(cardPayload(card));
          if (!flushTimer) flushTimer = setTimeout(flush, 750);
        } else if (preConsentImpressions.length < PENDING_CAP) {
          preConsentImpressions.push(cardPayload(card));
          // Reserve a chronological slot in the global pendingQueue on the first
          // pre-consent impression. At flush time the resolver returns the
          // consolidated items list, so view_item_list fires in the natural
          // user-journey position (before subsequent view_item / add_to_cart).
          if (!preConsentSlotReserved) {
            var reserved = enqueueDeferred('view_item_list', function () {
              if (!preConsentImpressions.length) return null;
              return {
                item_list_id: list.item_list_id,
                item_list_name: list.item_list_name,
                items: preConsentImpressions.splice(0)
              };
            });
            preConsentSlotReserved = !!reserved;
          }
        }
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.5 });

    function attachCards() {
      var cards = document.querySelectorAll('.card-wrapper[data-product-id]');
      cards.forEach(function (c) { io.observe(c); });
    }
    attachCards();

    // Re-scan after AJAX-driven filter/sort updates in Dawn
    var mo = new MutationObserver(function () { attachCards(); });
    var grid = document.getElementById('product-grid') || document.querySelector('.collection .product-grid, .search__results');
    if (grid) mo.observe(grid, { childList: true, subtree: true });

    // select_item on card click
    document.addEventListener('click', function (e) {
      var anchor = e.target.closest('.card-wrapper[data-product-id] a[href*="/products/"]');
      if (!anchor) return;
      var card = anchor.closest('.card-wrapper[data-product-id]');
      if (!card) return;
      push('select_item', {
        item_list_id: list.item_list_id,
        item_list_name: list.item_list_name,
        items: [cardPayload(card)]
      });
    });
  }

  // --- Custom events (micro-conversions) ---
  function trackCustomEvents() {
    // cart_drawer_open + view_cart when drawer opens
    var drawer = document.querySelector('cart-drawer');
    if (drawer) {
      var observer = new MutationObserver(function (mutations) {
        mutations.forEach(function (m) {
          if (m.type === 'attributes' && m.attributeName === 'class' && drawer.classList.contains('active')) {
            pushRaw({ event: 'cart_drawer_open' });
            pushViewCart();
          }
        });
      });
      observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }

    // free_shipping_bar_seen (once per session)
    if (!sessionStorage.getItem('glend_fsb_seen')) {
      var fsObserver = new MutationObserver(function () {
        if (document.querySelector('free-shipping-bar')) {
          pushRaw({ event: 'free_shipping_bar_seen' });
          sessionStorage.setItem('glend_fsb_seen', '1');
          fsObserver.disconnect();
        }
      });
      fsObserver.observe(document.body, { childList: true, subtree: true });
    }

    // upsell_click
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.cart-upsell__btn, [data-drawer-upsell-add]');
      if (!btn) return;
      var card = btn.closest('[data-product-title]') || btn.closest('.cart-upsell__card, .drawer-upsell__card');
      var title = card ? (card.dataset.productTitle || card.querySelector('.glend-rp__title, .drawer-upsell__title')?.textContent?.trim()) : '';
      pushRaw({ event: 'upsell_click', upsell_product: title || 'unknown' });
    });

    // sticky_atc_click
    document.addEventListener('click', function (e) {
      if (e.target.closest('.sticky-atc__button')) {
        pushRaw({ event: 'sticky_atc_click' });
      }
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    primeCartSnapshot();
    trackViewItem();
    trackCartMutations();
    trackBeginCheckout();
    trackViewCart();
    trackViewItemList();
    trackCustomEvents();
  }
})();
